import { useMemo } from "react";
import { scaleTime } from "d3-scale";
import dayjs from "dayjs";
import { Area, AreaChart, CartesianGrid, XAxis, YAxis } from "recharts";
import {
  ChartContainer,
  ChartLegend,
  ChartLegendContent,
  ChartTooltip,
  ChartTooltipContent,
} from "@/components/ui/chart";
import { ExploreInteractionType } from "@/v2/entities/explore/Explore.model";
import { Filter } from "@/v2/entities/explore/Explore.filter";
import { useExploreValueDistribution } from "@/v2/entities/explore/Explore.api";
import {
  ExploreChartEmptyState,
  ExploreChartLoader,
  getAggColumnConfig,
  normalizeValue,
} from "@/v2/features/explore-widgets/explore-widgets-utils";
import { ChartConfig } from "@/components/ui/chart";
import { ChartItem } from "@/v2/features/explore-widgets/useExploreDistributionChart";
import { ExploreGroupByColumnType } from "@/v2/entities/explore/columns/ExploreColum.model";
import { getColor } from "@/components/radixColors";
import { Flex } from "@radix-ui/themes";
import { TruncatedTick } from "@/v2/features/explore-widgets/TruncatedTick";

const chartConfig: ChartConfig = {
  [normalizeValue("avgQaScore")]: {
    label: "Avg. QA Score",
    color: getColor(),
  },
  [normalizeValue("avgAutoQaScore")]: {
    label: "Avg. Auto QA Score",
    color: getColor(5),
  },
};

const values: string[] = ["avgQaScore", "avgAutoQaScore"];
const valueMap = new Map<string, string>(
  values.map((value) => [normalizeValue(value), value])
);

interface QaScoreDeltaProps {
  source: ExploreInteractionType;
  filter: Filter;
  groupBy: ExploreGroupByColumnType;
}

export function QaScoreDelta({
  source,
  filter,
  groupBy = "week",
}: QaScoreDeltaProps) {
  const avgQaScoreDistribution = useExploreValueDistribution({
    source,
    groupBy,
    splitBy: "tenantId",
    metric: "avgQaScore",
    filter,
  });

  const avgAutoQaScoreDistribution = useExploreValueDistribution({
    source,
    groupBy,
    splitBy: "tenantId",
    metric: "avgAutoQaScore",
    filter,
  });

  const isLoading = useMemo(
    () =>
      avgQaScoreDistribution.isLoading || avgAutoQaScoreDistribution.isLoading,
    [avgQaScoreDistribution.isLoading, avgAutoQaScoreDistribution.isLoading]
  );

  const results = useMemo(() => {
    if (
      avgQaScoreDistribution.data == null ||
      avgAutoQaScoreDistribution.data == null
    ) {
      return [];
    }

    const resultMap: Record<
      string,
      Record<string, number | string | null>
    > = {};
    avgQaScoreDistribution.data.results.forEach((result) => {
      const groupByValue = result[groupBy];

      if (groupByValue == null || typeof groupByValue !== "string") {
        return;
      }

      resultMap[groupByValue] = {
        [groupBy]: groupByValue,
        avgQaScore: result.avgQaScore,
      };
    });

    avgAutoQaScoreDistribution.data.results.forEach((result) => {
      const groupByValue = result[groupBy];

      if (groupByValue == null || typeof groupByValue !== "string") {
        return;
      }

      if (resultMap[groupByValue] == null) {
        resultMap[groupByValue] = {
          [groupBy]: groupByValue,
          avgAutoQaScore: result.avgAutoQaScore,
        };
      } else {
        resultMap[groupByValue].avgAutoQaScore = result.avgAutoQaScore;
      }
    });

    return Object.values(resultMap);
  }, [avgQaScoreDistribution.data, avgAutoQaScoreDistribution.data]);

  const items = useMemo(() => {
    const items: ChartItem[] = [];

    results.forEach((item) => {
      const groupByValue = item[groupBy];
      if (groupByValue == null) {
        return;
      }

      const values: [string, number][] = [];
      if (item.avgQaScore != null && typeof item.avgQaScore === "number") {
        values.push([normalizeValue("avgQaScore"), item.avgQaScore]);
      }
      if (
        item.avgAutoQaScore != null &&
        typeof item.avgAutoQaScore === "number"
      ) {
        values.push([normalizeValue("avgAutoQaScore"), item.avgAutoQaScore]);
      }

      items.push({
        value: groupByValue.toString(),
        values,
      });
    });

    return items;
  }, [results]);

  const { xDomain, ticks, formatDate } = useMemo(() => {
    // Determine the minimum and maximum timestamps from the data.
    const timestamps: number[] = items.map((item) =>
      new Date(item.value).getTime()
    );

    const minTimestamp: number = Math.min(...timestamps);
    const maxTimestamp: number = Math.max(...timestamps);
    const xDomain = [minTimestamp, maxTimestamp];

    const dateFormat =
      dayjs.duration(maxTimestamp - minTimestamp).asYears() > 1
        ? "MMM DD"
        : "MMM DD";

    const formatDate = (timestamp: number): string =>
      dayjs(timestamp).format(dateFormat);

    if (groupBy === "week") {
      const ticks: number[] = timestamps.sort((a, b) => a - b);
      return { chartConfig, xDomain, ticks, formatDate };
    }

    // Create a D3 time scale using the computed min and max timestamps.
    // This scale is used here to generate tick values.
    const timeScale = scaleTime()
      .domain([new Date(minTimestamp), new Date(maxTimestamp)])
      .range([minTimestamp, maxTimestamp]);

    // Generate tick values (displaying 5 ticks on the x-axis).
    const ticks: number[] = timeScale.ticks(12).map((date) => date.getTime());

    return { chartConfig, xDomain, ticks, formatDate };
  }, [items]);

  const chartData = useMemo(() => {
    return items.map(({ value, values, ...rest }) => ({
      ...rest,
      timestamp: new Date(value).getTime(),
      ...Object.fromEntries(values.map(([key, value]) => [key, value])),
    }));
  }, [items]);

  const {
    valueFormatter: valueFormatter,
    labelFormatter: yTickLabelFormatter,
    domain: yDomain,
  } = useMemo(() => {
    return getAggColumnConfig("avgQaScore");
  }, []);

  if (isLoading) {
    return <ExploreChartLoader />;
  }

  if (chartData.length === 0) {
    return <ExploreChartEmptyState variant="no-data" />;
  }

  return (
    <Flex direction="column" gap="2" className="w-full">
      <Flex flexGrow={"1"} height="0">
        <ChartContainer config={chartConfig} className="w-full h-full">
          <AreaChart
            data={chartData}
            margin={{ left: 8, top: 8, right: 8, bottom: 8 }}
          >
            <defs>
              {Array.from(valueMap.keys()).map((value) => (
                <linearGradient
                  key={value}
                  id={`fill${value}`}
                  x1="0"
                  y1="0"
                  x2="0"
                  y2="1"
                >
                  <stop
                    offset="5%"
                    stopColor={`var(--color-${value})`}
                    stopOpacity={0.8}
                  />
                  <stop
                    offset="95%"
                    stopColor={`var(--color-${value})`}
                    stopOpacity={0.1}
                  />
                </linearGradient>
              ))}
            </defs>
            <CartesianGrid vertical={false} />
            <XAxis
              dataKey="timestamp"
              type="number"
              domain={xDomain}
              ticks={ticks}
              tickFormatter={formatDate}
              tickLine={false}
              axisLine={false}
              tickMargin={8}
            />
            <YAxis
              interval={0}
              minTickGap={6}
              tickLine={false}
              axisLine={false}
              height={48}
              domain={yDomain}
              tick={
                <TruncatedTick
                  axis="y"
                  anchor="middle"
                  dx={-16}
                  slotWidth={64}
                  pad={4}
                  labelFormatter={yTickLabelFormatter}
                />
              }
            />
            <ChartTooltip
              cursor={false}
              content={
                <ChartTooltipContent
                  valueFormatter={(value) =>
                    typeof value === "number" ? valueFormatter?.(value) : value
                  }
                  labelGetter={(payload) => formatDate(payload.timestamp)}
                  indicator="dot"
                />
              }
            />
            {Array.from(valueMap.keys()).map((value) => (
              <Area
                key={value}
                dataKey={value}
                type={"linear"}
                unit={"avgQaScore"}
                stroke={`var(--color-${value})`}
                fill={"transparent"}
              />
            ))}
            <ChartLegend content={<ChartLegendContent />} />
          </AreaChart>
        </ChartContainer>
      </Flex>
    </Flex>
  );
}
